import type { OrderType } from '@/models';

/**
 * AB#20570 — the order-type catalogue.
 *
 * MIRRORS App\Enums\OrderTypeSymbol in the backend, which is now the source of truth. Two
 * real bugs are fixed here rather than carried over:
 *
 *   1. `Si` appeared TWICE, once as "Sample (with invoice)" and once as
 *      "Sample (no invoice)". Selecting the second wrote `Si` to the order, so a sample
 *      without an invoice was indistinguishable from one with an invoice. The no-invoice
 *      sample is `S`, which is what the backend and the existing dashboard filter
 *      (AdminDashboardController filters on [S, Si]) already expect.
 *   2. `Ci` and `Coi` were labelled "(no invoice)", but the trailing "i" in this scheme
 *      MEANS invoiced. The suffix is dropped rather than corrected to "(with invoice)",
 *      because operations refer to these simply as Compensation and Commission offset.
 *
 * `requiresOriginalOrder` drives the original-order field in PriceBreakdownModal: a remake
 * or reorder has to say which order it replaces (AB#20572).
 */
export const orderTypes: OrderType[] = [
  {
    symbol: 'Oi',
    label: 'Order (with invoice)',
    default: true,
  },
  {
    symbol: 'Ri',
    label: 'Remake (with invoice)',
    default: false,
  },
  {
    symbol: 'R',
    label: 'Remake (no invoice)',
    default: false,
  },
  {
    symbol: 'REORDER',
    label: 'Reorder',
    default: false,
  },
  {
    symbol: 'Si',
    label: 'Sample (with invoice)',
    default: false,
  },
  {
    symbol: 'S',
    label: 'Sample (no invoice)',
    default: false,
  },
  {
    symbol: 'Ci',
    label: 'Compensation',
    default: false,
  },
  {
    symbol: 'Coi',
    label: 'Commission offset',
    default: false,
  },
];

/** Types that must name the order they replace. Keep aligned with OrderTypeSymbol::requiresOriginalOrder(). */
export const ORDER_TYPES_REQUIRING_ORIGINAL_ORDER = ['Ri', 'R', 'REORDER'] as const;

export function orderTypeRequiresOriginalOrder(symbol?: string | null): boolean {
  if (!symbol) return false;
  return (ORDER_TYPES_REQUIRING_ORIGINAL_ORDER as readonly string[]).includes(symbol.trim());
}

/** Types that count against the remake rate. Keep aligned with OrderTypeSymbol::countsAsFailure(). */
export function orderTypeCountsAsFailure(symbol?: string | null): boolean {
  if (!symbol) return false;
  return ['Ri', 'R'].includes(symbol.trim());
}

/**
 * Resolve a stored symbol to its catalogue entry, or undefined for a legacy value this
 * list does not know. Never throws: 13 233 order rows predate any validation, so a symbol
 * that is not here must still render rather than blank the selector.
 */
export function findOrderType(symbol?: string | null): OrderType | undefined {
  if (!symbol) return undefined;
  return orderTypes.find((type) => type.symbol === symbol.trim());
}
